import PortalIdParser from 'PortalIdParser';
import http from 'hub-http/clients/apiClient';
import { PromiseClient } from 'hub-http/adapters/promiseClient';

type CrmSearchResultsPropertyVersionsType = {
  name: string;
  source: string;
  sourceId: string;
  sourceMetadata: string;
  sourceVid: number[];
  timestamp: number;
  value: string;
};

type CrmSearchResultsPropertyType = {
  source: string;
  sourceId: string;
  timestamp: number;
  updatedByUserId: number | null;
  value: string;
  versions: CrmSearchResultsPropertyVersionsType[];
};

type CRMSearchObject = {
  objectId: number;
  objectTypeId: string;
  portalId: number;
  properties: { [key: string]: CrmSearchResultsPropertyType | undefined };
  objectVersion: { version: number; timestamp: number };
  objectStates: {
    state: string;
    timestamp: number;
  }[];
  reverseReferences: number[];
  mergeAudits: string | string[];
  secondaryIdentifier: string | null;
  currentUserPermissions: {
    canEdit: boolean;
    canDelete: boolean;
    canView: boolean;
    canCommunicate: boolean;
  } | null;
  state: {
    state: string;
    timestamp: number;
  };
};

type CRMSearchInvoice = CRMSearchObject & {
  associatedObjects: { [key: string]: CRMSearchObject[] };
  properties: { [key: string]: CrmSearchResultsPropertyType };
};

export type InvoicesRequestResponse = {
  hasMore: boolean;
  offset: number;
  total: number;
  results: CRMSearchInvoice[];
};

type QueryOptions = {
  count: number;
  offset: number;
  query: string;
};

type Invoice = {
  associatedObjects: {};
  objectId: number;
  objectTypeId: '0-53';
  portalId: number;
  properties: Record<string, any>[];
};

export const makeInvoiceSearchQuery = ({
  count,
  offset,
  query,
}: QueryOptions) => {
  return {
    count,
    filterGroups: [
      {
        filters: [
          {
            property: 'hs_invoice_source',
            operator: 'EQ',
            value: 'native_invoice',
          },
          {
            property: 'hs_invoice_status',
            values: ['open', 'paid', 'voided'],
            operator: 'IN',
          },
        ],
      },
    ],
    objectTypeId: '0-53',
    offset,
    query,
    portalId: PortalIdParser.get(),
    requestOptions: {
      allPropertiesFetchMode: 'latest_version',
      includeAllValues: true,
    },
    associationPreviews: {
      INVOICE_TO_CONTACT: {
        properties: ['firstName', 'lastName', 'email'],
      },
    },
    sorts: [
      {
        order: 'DESC',
        direction: 1,
        property: 'hs_createdate',
        sortKey: 'hs_createdate',
      },
    ],
  };
};

export const defaultApiWrapper = (apiClient: PromiseClient) => ({
  searchInvoices({ count, offset, query }: QueryOptions) {
    return apiClient.post<InvoicesRequestResponse>('crm-search/search', {
      data: { ...makeInvoiceSearchQuery({ count, offset, query }) },
    });
  },
  fetchRenderedContent({ id }: { id: number }) {
    return apiClient
      .get<Invoice>(`invoices/v1/invoices/${id}/with-associated-objects`)
      .then((invoice) => {
        const invoiceName = (
          invoice.properties.find(
            (property) => property.name === 'hs_number'
          ) as Record<string, any>
        ).value;
        const domain = (
          invoice.properties.find(
            (property) => property.name === 'hs_domain'
          ) as Record<string, any>
        ).value;
        const slug = (
          invoice.properties.find(
            (property) => property.name === 'hs_slug'
          ) as Record<string, any>
        ).value;
        const invoiceUrl = `https://${domain}/${slug}`;

        return {
          body: invoiceName,
          htmlBody: `<a href=${invoiceUrl}>${invoiceName}</a>`,
        };
      });
  },
});

export const searchInvoices = ({ count, offset, query }: QueryOptions) => {
  return http.post<InvoicesRequestResponse>('crm-search/search', {
    data: { ...makeInvoiceSearchQuery({ count, offset, query }) },
  });
};
